import api from './base-api';
import {
	CreateSecondChanceMarketDto,
	MakeSecondChanceTradeDto,
	SettleSecondChanceMarketDto,
	UpdateSecondChanceMarketDto,
	SecondChanceMarketDto,
} from '@sportspredict/shared';

export const secondChanceMarketsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getSecondChanceMarkets: builder.query<SecondChanceMarketDto[], string>({
			query: (lobbyId) => ({
				url: `/second-chance-markets`,
				params: { lobby_id: lobbyId },
			}),
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({ type: 'SecondChanceMarket' as const, id })),
							{ type: 'SecondChanceMarket' as const },
						]
					: [{ type: 'SecondChanceMarket' as const }],
		}),
		createSecondChanceMarket: builder.mutation<SecondChanceMarketDto, CreateSecondChanceMarketDto>({
			query: (data) => ({
				url: '/second-chance-markets',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: [{ type: 'SecondChanceMarket' as const }],
		}),
		updateSecondChanceMarket: builder.mutation<SecondChanceMarketDto, UpdateSecondChanceMarketDto>({
			query: ({ id, ...patch }) => ({
				url: `/second-chance-markets/${id}`,
				method: 'PUT',
				body: patch,
			}),
			invalidatesTags: (result, error, { id }) => [{ type: 'SecondChanceMarket' as const, id }],
		}),
		settleSecondChanceMarket: builder.mutation<SecondChanceMarketDto, SettleSecondChanceMarketDto>({
			query: (body) => ({
				url: `/second-chance-markets/settle`,
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'SecondChanceMarket' as const }],
		}),
		deleteSecondChanceMarket: builder.mutation<void, string>({
			query: (id) => ({
				url: `/second-chance-markets/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [{ type: 'SecondChanceMarket' as const }],
		}),

		// User-facing D2 endpoints
		createSecondChanceTrade: builder.mutation<unknown, MakeSecondChanceTradeDto>({
			query: (data) => ({
				url: '/second-chance-markets/trades',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: [
				{ type: 'SecondChanceMarket' as const },
				{ type: 'SecondChanceTrade' as const },
			],
		}),
		getSecondChanceTrades: builder.query<
			Array<{
				id: string;
				second_chance_market_id: string;
				value: number;
				user_id: string;
				lobby_id: string;
				created_date: string;
			}>,
			{ lobbyId: string }
		>({
			query: ({ lobbyId }) => ({
				url: '/second-chance-markets/trades',
				params: { lobby_id: lobbyId },
			}),
			providesTags: [{ type: 'SecondChanceTrade' as const }],
		}),
		getSecondChanceRank: builder.query<{ rank: number; totalGap: number }, { lobbyId: string }>({
			query: ({ lobbyId }) => ({
				url: '/second-chance-markets/rank',
				params: { lobby_id: lobbyId },
			}),
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetSecondChanceMarketsQuery,
	useCreateSecondChanceMarketMutation,
	useUpdateSecondChanceMarketMutation,
	useSettleSecondChanceMarketMutation,
	useDeleteSecondChanceMarketMutation,
	useCreateSecondChanceTradeMutation,
	useGetSecondChanceTradesQuery,
	useGetSecondChanceRankQuery,
} = secondChanceMarketsApi;

export default secondChanceMarketsApi;
